/**
 * Service worker registration and a deliberately conservative update policy.
 *
 * A pinned device may never close the app, so waiting for "all tabs closed" can
 * mean never updating. But reloading while a child is mid-drag is worse than
 * being a version behind. So: pick up the new worker in the background, then
 * activate it only once nobody has touched the screen for a while.
 */

const CHECK_INTERVAL_MS = 30 * 60 * 1000; // re-check every 30 minutes
const IDLE_BEFORE_RELOAD_MS = 60 * 1000; // and only after a minute of stillness

export function installUpdater(swUrl = 'sw.js'): void {
  if (!('serviceWorker' in navigator)) return;
  // No service worker is built in dev, so the dev server answers /sw.js with
  // index.html and the registration fails noisily. Nothing here is testable
  // without a real build anyway - use `deno task preview` for that.
  if (import.meta.env.DEV) return;

  // Ask for durable storage so Chrome's eviction under disk pressure can never
  // silently empty the precache on a device that is offline for weeks.
  void navigator.storage?.persist?.().catch(() => {});

  let lastInteraction = Date.now();
  const touch = () => {
    lastInteraction = Date.now();
  };
  for (const type of ['pointerdown', 'pointermove', 'keydown']) {
    globalThis.addEventListener(type, touch, { passive: true });
  }

  globalThis.addEventListener('load', () => {
    navigator.serviceWorker
      .register(new URL(swUrl, document.baseURI).href, { scope: './' })
      .then((registration) => {
        // Reload once the new worker has actually taken control, so the page
        // and its assets always come from the same version.
        let reloading = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (reloading) return;
          reloading = true;
          location.reload();
        });

        const applyWhenIdle = () => {
          const waiting = registration.waiting;
          if (!waiting) return;
          const idleFor = Date.now() - lastInteraction;
          const hidden = document.visibilityState === 'hidden';
          if (hidden || idleFor > IDLE_BEFORE_RELOAD_MS) {
            waiting.postMessage({ type: 'SKIP_WAITING' });
          }
        };

        registration.addEventListener('updatefound', () => {
          const installing = registration.installing;
          installing?.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              applyWhenIdle();
            }
          });
        });

        // Check on launch, then on a slow timer, then whenever we come back to
        // the foreground (a pinned device spends most of its life backgrounded).
        const check = () => registration.update().catch(() => {});
        void check();
        setInterval(() => {
          void check();
          applyWhenIdle();
        }, CHECK_INTERVAL_MS);
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') void check();
          else applyWhenIdle();
        });

        // A worker that arrived while the app was closed is already waiting.
        setInterval(applyWhenIdle, 15 * 1000);
      })
      .catch(() => {
        // No service worker means no offline support, but the app still runs.
      });
  });
}
